import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface Organisation {
  id: string;
  name: string;
  slug: string;
  sku: string;
  billing_status: 'trial' | 'active' | 'suspended' | 'cancelled';
  trial_ends_at: string | null;
  max_seats: number;
  current_seats: number;
  is_demo: boolean;
  is_internal: boolean;
  created_by: string;
}

export interface OrgMembership {
  id: string;
  organisation_id: string;
  role: 'owner' | 'admin' | 'manager' | 'viewer';
  internal_role: 'global_admin' | 'staff' | 'guest' | null;
  is_active: boolean;
  organisation: Organisation;
}

export const useOrganisation = () => {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<OrgMembership[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organisation | null>(null);
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [currentInternalRole, setCurrentInternalRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setMemberships([]);
      setCurrentOrg(null);
      setCurrentRole(null);
      setCurrentInternalRole(null);
      setLoading(false);
      return;
    }

    const fetchMemberships = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('organisation_members')
        .select('id, organisation_id, role, internal_role, is_active, organisations(*)')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching org memberships:', error);
        setLoading(false);
        return;
      }

      const mapped: OrgMembership[] = (data || []).map((m: any) => ({
        id: m.id,
        organisation_id: m.organisation_id,
        role: m.role,
        internal_role: m.internal_role || null,
        is_active: m.is_active,
        organisation: m.organisations as Organisation,
      }));

      const getMembershipPriority = (membership: OrgMembership) => {
        if (membership.internal_role === 'global_admin') return 100;
        if (membership.internal_role === 'staff') return 90;
        if (membership.internal_role === 'guest') return 80;
        if (membership.organisation?.is_internal) return 70;
        if (membership.role === 'owner') return 60;
        if (membership.role === 'admin') return 50;
        if (membership.role === 'manager') return 40;
        return 30;
      };

      const prioritizedMemberships = [...mapped].sort(
        (a, b) => getMembershipPriority(b) - getMembershipPriority(a)
      );

      setMemberships(prioritizedMemberships);

      const selectedMembership = currentOrg
        ? prioritizedMemberships.find((membership) => membership.organisation_id === currentOrg.id) ?? prioritizedMemberships[0]
        : prioritizedMemberships[0];

      if (selectedMembership) {
        setCurrentOrg(selectedMembership.organisation);
        setCurrentRole(selectedMembership.role);
        setCurrentInternalRole(selectedMembership.internal_role);
      }

      setLoading(false);
    };

    fetchMemberships();
  }, [user]);

  const selectOrg = (orgId: string) => {
    const membership = memberships.find(m => m.organisation_id === orgId);
    if (membership) {
      setCurrentOrg(membership.organisation);
      setCurrentRole(membership.role);
      setCurrentInternalRole(membership.internal_role);
    }
  };

  const isOwnerOrAdmin = currentRole === 'owner' || currentRole === 'admin';
  const isManager = currentRole === 'manager' || isOwnerOrAdmin;
  const isDemo = currentOrg?.is_demo ?? false;
  const hasOrg = memberships.length > 0;

  return {
    memberships,
    currentOrg,
    currentRole,
    currentInternalRole,
    selectOrg,
    isOwnerOrAdmin,
    isManager,
    isDemo,
    hasOrg,
    loading,
  };
};
